import { AppError } from '../error';
import {
  getDefaultError,
  getParseError,
  getDataValidationError,
} from '../utils';
import { logApiRequest, logApiResponse, logError, logWarn } from '../logger';
import { apiBaseUrl } from './apiConsts';
import type { ErrorResponse, PagedResponse } from '../types';

export interface FetchSuccess<T> {
  success: true;
  data: T;
  error?: undefined;
  status: number;
  durationMs: number;
}

export interface FetchFailure {
  success: false;
  data: null;
  error: AppError;
  status?: number;
  durationMs?: number;
}

export type FetchResult<T> = FetchSuccess<T> | FetchFailure;

type ResponseValidator<T> = (data: any) => data is T;

const createNoopValidator = <T>(): ResponseValidator<T> => {
  return (_: unknown): _ is T => true;
};

interface NormalizedRequestConfig<T> {
  url: string;
  requestInit?: RequestInit;
  validate?: ResponseValidator<T>;
  retries: number;
  timeoutMs: number;
  expectBody: boolean;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const DEFAULT_TIMEOUT_MS = 20000; // 20 seconds
export const DEFAULT_NUM_OF_RETRIES = 5;
const DEFAULT_RETRY_DELAY_MS = 2000;

function shortenApiUrl(url: string): string {
  // Find the first occurrence of 'api/' and return from there
  const idx = url.indexOf('api/');
  if (idx !== -1) return url.substring(idx);
  // fallback: strip apiBaseUrl if present
  if (url.startsWith(apiBaseUrl)) return url.substring(apiBaseUrl.length);
  return url;
}

function isErrorResponse(payload: any): payload is ErrorResponse {
  return (
    payload &&
    typeof payload === 'object' &&
    typeof payload.message === 'string' &&
    typeof payload.status === 'number'
  );
}

const toSuccess = <T>(
  data: T,
  status: number,
  durationMs: number,
): FetchSuccess<T> => ({
  success: true,
  data,
  status,
  durationMs,
});

const toFailure = (
  error: AppError,
  status?: number,
  durationMs?: number,
): FetchFailure => ({
  success: false,
  data: null,
  error,
  status,
  durationMs,
});

function normalizeConfig<T>(
  url: string,
  requestInit?: RequestInit,
  validate?: ResponseValidator<T>,
  retries: number = DEFAULT_NUM_OF_RETRIES,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  expectBody: boolean = true,
): NormalizedRequestConfig<T> {
  return {
    url,
    requestInit,
    validate,
    retries,
    timeoutMs,
    expectBody,
  };
}

export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    logApiRequest(options.method || 'GET', shortenApiUrl(url));
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

export async function getDataWithRetry<T>(
  url: string,
  requestOptions: RequestInit,
  isValid: ResponseValidator<T>,
  numOfRetries: number = DEFAULT_NUM_OF_RETRIES,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  // Deprecated: expect JSON payload flag. Automatically handled when possible.
  shouldRespondWithData: boolean = true,
): Promise<FetchResult<T>> {
  const config = normalizeConfig<T>(
    url,
    requestOptions,
    isValid,
    numOfRetries,
    timeoutMs,
    shouldRespondWithData,
  );

  for (let attempt = 1; attempt <= config.retries; attempt++) {
    try {
      const startTime = Date.now();
      const response = await fetchWithTimeout(
        config.url,
        config.requestInit,
        config.timeoutMs,
      );
      const durationMs = Date.now() - startTime;

      const rawBody = await response.text();
      const trimmedBody = rawBody.trim();
      const hasBody = trimmedBody.length > 0;
      let parsedBody: any = null;

      if (hasBody) {
        if (!config.expectBody) {
          try {
            parsedBody = JSON.parse(trimmedBody);
          } catch {
            // Some associate endpoints return plain text bodies. Since callers
            // opted into `expectBody=false`, swallow parse errors and continue.
            parsedBody = null;
          }
        } else {
          try {
            parsedBody = JSON.parse(trimmedBody);
          } catch {
            logError(
              `Parse error: ${
                config.requestInit?.method || 'GET'
              } ${shortenApiUrl(config.url)}`,
            );
            throw getParseError(config.url);
          }
        }
      } else if (!config.expectBody) {
        parsedBody = null;
      }

      if (!response.ok) {
        const errorPayload = isErrorResponse(parsedBody)
          ? parsedBody
          : {
              error: 'HttpError',
              message:
                parsedBody?.message ||
                response.statusText ||
                'Unknown error occurred',
              status: response.status || 500,
              path: config.url,
              timestamp: new Date().toISOString(),
            };

        throw new AppError(errorPayload);
      }

      if (config.expectBody && config.validate && !hasBody) {
        logError(
          `Missing response body: ${
            config.requestInit?.method || 'GET'
          } ${shortenApiUrl(config.url)}`,
        );
        throw getDataValidationError(config.url);
      }

      if (
        parsedBody !== null &&
        config.validate &&
        !config.validate(parsedBody)
      ) {
        logError(
          `Invalid response: ${
            config.requestInit?.method || 'GET'
          } ${shortenApiUrl(config.url)}`,
        );
        throw getDataValidationError(config.url);
      }

      logApiResponse(
        config.requestInit?.method || 'GET',
        shortenApiUrl(config.url),
        durationMs,
      );
      return toSuccess((parsedBody ?? null) as T, response.status, durationMs);
    } catch (error) {
      const isLastAttempt = attempt === config.retries;
      const isDuplicatePinError =
        error instanceof AppError &&
        error.status === 400 &&
        error.message.toLowerCase().includes('pin already in use');
      const method = config.requestInit?.method || 'GET';

      if (isLastAttempt || isDuplicatePinError) {
        const appError =
          error instanceof AppError ? error : getDefaultError(config.url);
        if (isLastAttempt) {
          logError(
            `Final error: ${method} ${shortenApiUrl(config.url)} (${appError.name})`,
          );
        }
        return toFailure(appError);
      }

      logWarn(
        `Retry ${attempt} failed: ${method} ${shortenApiUrl(config.url)}`,
      );
      await sleep(DEFAULT_RETRY_DELAY_MS);
    }
  }

  logError(
    `Unrecoverable error: ${config.requestInit?.method || 'GET'} ${shortenApiUrl(
      config.url,
    )}`,
  );
  return toFailure(getDefaultError(config.url));
}

export function sendWithoutBody(
  url: string,
  requestOptions: RequestInit,
  numOfRetries: number = DEFAULT_NUM_OF_RETRIES,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<FetchResult<null>> {
  return getDataWithRetry<null>(
    url,
    requestOptions,
    createNoopValidator<null>(),
    numOfRetries,
    timeoutMs,
    false,
  );
}

export function mapPagedResult<T>(
  result: FetchResult<PagedResponse<T>>,
): FetchResult<T[]> {
  if (!result.success) {
    return result;
  }

  return {
    ...result,
    data: result.data.content ?? [],
  };
}
